// ---------------------------------------------------------------------------
// Block Editor — block palette + tree sidebar (BlockTree) and a floating,
// contextual config panel (ConfigPanel).
//
// The two are split so the tree owns the full-height sidebar while the config
// form floats over the canvas (PowerPoint-style) only when a block is selected
// — giving the form all the vertical room it needs without starving the tree.
// Selection is CONTROLLED by the parent: a single source of truth
// (clickedBlockId in VizDslTestPage) flows into `selectedId`, and both
// components report selection changes back via `onSelect`/`onClose`. That also
// means a tree click now highlights the block on the canvas (it didn't before).
//
// Pure DSL-mutation helpers live at module scope so both components share one
// implementation (add/remove/setNestedValue). `ensureIds` is owned by BlockTree,
// which propagates minted IDs back to the parent; ConfigPanel resolves the
// selected block from a locally-ensured copy (idempotent, harmless).
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import FieldEditor from "./FieldEditor.jsx";
import { blockRegistry } from "./shared/block-registry/index.js";
import "./shared/block-registry/init.js";
import "./blockRegistryInit.js";

// ── helpers ──────────────────────────────────────────────────────────────

const CHILD_KEYS = ["flow", "children", "blocks", "then", "branches", "bands", "columns"];

// Definition `icon` strings are bare Lucide names (e.g. "chart-bar"). Map the
// ones that don't map 1:1 to a valid `lucide:*` icon; everything else falls
// back to `lucide:<name>`.
const ICON_MAP = {
  "chart-bar": "lucide:bar-chart-3",
  columns: "lucide:columns-3",
  layout: "lucide:layout-template",
  text: "lucide:type",
  edit: "lucide:square-pen",
};
const resolveBlockIcon = (name) => {
  if (!name) return "lucide:box";
  return ICON_MAP[name] || `lucide:${name}`;
};

// Per-category accent: icon chip background + foreground. Mirrors the app's
// ccgreen/ccblue/ccpurple tokens and Tailwind's default slate/amber/rose.
const CATEGORY_STYLES = {
  "data-visualization": "text-ccblue-700 bg-ccblue-50",
  data: "text-ccpurple-700 bg-ccpurple-50",
  text: "text-amber-700 bg-amber-50",
  layout: "text-slate-600 bg-slate-100",
  "layout-container": "text-ccgreen-700 bg-ccgreen-50",
  media: "text-rose-700 bg-rose-50",
};
const categoryStyleFor = (blockType) => {
  const def = blockRegistry.getIfRegistered(blockType);
  const cat = def?.editor?.category;
  return CATEGORY_STYLES[cat] || CATEGORY_STYLES.layout;
};

// Walk the DSL and mint IDs for any node that is missing one. Returns true if
// anything changed (so the caller knows whether to propagate back via onDSLChange).
function ensureIds(node) {
  if (!node || typeof node !== "object") return false;
  if (Array.isArray(node)) {
    return node.reduce((changed, child) => ensureIds(child) || changed, false);
  }
  let changed = false;
  if (node.type && !node.id) {
    node.id = `${node.type}_${Math.random().toString(36).slice(2, 9)}`;
    changed = true;
  }
  for (const key of CHILD_KEYS) {
    if (node[key] !== undefined && ensureIds(node[key])) changed = true;
  }
  return changed;
}

// Remove a block by ID from anywhere in the DSL tree. Mutates in place.
function removeById(node, targetId) {
  if (!node || typeof node !== "object") return false;
  for (const key of CHILD_KEYS) {
    const val = node[key];
    if (!val) continue;
    if (Array.isArray(val)) {
      const idx = val.findIndex(c => c && c.id === targetId);
      if (idx !== -1) { val.splice(idx, 1); return true; }
      for (const child of val) { if (child && removeById(child, targetId)) return true; }
    } else if (typeof val === "object") {
      if (removeById(val, targetId)) return true;
    }
  }
  return false;
}

// Find all ancestor container IDs for a target block. Returns array of IDs
// starting from the root down to the immediate parent of the target.
function findAncestors(node, targetId, path = []) {
  if (!node || typeof node !== "object") return null;
  if (Array.isArray(node)) {
    for (const child of node) {
      const result = findAncestors(child, targetId, path);
      if (result) return result;
    }
    return null;
  }
  if (node.id === targetId) return path;
  const isContainer = node.type === "flow" || node.type === "band" || node.type === "conditional" || (!node.type && (node.columns || node.blocks));
  for (const key of CHILD_KEYS) {
    const val = node[key];
    if (val === undefined) continue;
    const subPath = isContainer && node.id ? [...path, node.id] : path;
    const result = findAncestors(val, targetId, subPath);
    if (result) return result;
  }
  return null;
}

function collectBlocks(node, list = []) {
  if (!node || typeof node !== "object") return list;
  if (Array.isArray(node)) { node.forEach(n => collectBlocks(n, list)); return list; }
  if (node.type && node.type !== "flow" && node.type !== "band" && node.id) {
    list.push(node);
  }
  for (const key of CHILD_KEYS) {
    if (node[key] !== undefined) collectBlocks(node[key], list);
  }
  return list;
}

// ── pure DSL mutations (shared by BlockTree + ConfigPanel) ──────────────

// Deep-copy + ensure IDs. Used by both components to get an ID-stable view of
// the DSL for resolution; ensureIds is idempotent so running it twice is safe.
function withEnsuredIds(dsl) {
  const copy = JSON.parse(JSON.stringify(dsl));
  ensureIds(copy);
  return copy;
}

// Insert a new block of `blockType` into the first vertical flow / band column,
// falling back to the root flow. Returns { dsl: newDSL, newId }.
function addBlockToDsl(dsl, blockType) {
  const def = blockRegistry.get(blockType);
  const template = def.editor?.defaultTemplate || { type: blockType };
  const newBlock = { ...template, id: `${blockType}_${Date.now()}` };
  const newDSL = JSON.parse(JSON.stringify(dsl));

  const insertInto = (node) => {
    if (node.type === "flow" && node.direction === "vertical") {
      if (!node.children) node.children = [];
      node.children.push(newBlock);
      return true;
    }
    for (const key of CHILD_KEYS) {
      const val = node[key];
      if (Array.isArray(val)) {
        for (const child of val) { if (insertInto(child)) return true; }
      } else if (val && typeof val === "object") {
        if (insertInto(val)) return true;
      }
    }
    return false;
  };

  if (!insertInto(newDSL)) {
    if (newDSL.bands && newDSL.bands.length > 0) {
      const lastBand = newDSL.bands[newDSL.bands.length - 1];
      if (lastBand.columns && lastBand.columns.length > 0) {
        if (!lastBand.columns[0].blocks) lastBand.columns[0].blocks = [];
        lastBand.columns[0].blocks.push(newBlock);
      }
    } else {
      if (!newDSL.flow) newDSL.flow = [];
      if (!Array.isArray(newDSL.flow)) newDSL.flow = [newDSL.flow];
      newDSL.flow.push(newBlock);
    }
  }
  return { dsl: newDSL, newId: newBlock.id };
}

// Remove a block by ID. Returns a new DSL, or null if the block wasn't found.
function removeBlockFromDsl(dsl, blockId) {
  const newDSL = JSON.parse(JSON.stringify(dsl));
  return removeById(newDSL, blockId) ? newDSL : null;
}

// Set a nested value on the block matching blockId. `path` is an array of keys.
function setNestedValueInDsl(dsl, blockId, path, value) {
  const newDSL = JSON.parse(JSON.stringify(dsl));
  const update = (node) => {
    if (!node || typeof node !== "object") return false;
    if (node.id === blockId) {
      let target = node;
      for (let i = 0; i < path.length - 1; i++) {
        if (!target[path[i]] || typeof target[path[i]] !== "object") target[path[i]] = {};
        target = target[path[i]];
      }
      target[path[path.length - 1]] = value;
      return true;
    }
    for (const key of CHILD_KEYS) {
      const val = node[key];
      if (Array.isArray(val)) { for (const child of val) { if (update(child)) return true; } }
      else if (val && typeof val === "object") { if (update(val)) return true; }
    }
    return false;
  };
  update(newDSL);
  return newDSL;
}

// ── shared field visibility (showIf) ────────────────────────────────────

function visibleFieldsFor(block) {
  const def = blockRegistry.getIfRegistered(block.type);
  const fields = def?.editor?.configFields || [];
  const defaults = {};
  for (const f of fields) if (f.defaultValue !== undefined) defaults[f.name] = f.defaultValue;
  const resolve = (prop) => (block[prop] !== undefined ? block[prop] : defaults[prop]);
  return fields.filter((field) => {
    if (!field.showIf) return true;
    return Object.entries(field.showIf).every(([prop, allowed]) => {
      const vals = Array.isArray(allowed) ? allowed : [allowed];
      return vals.includes(resolve(prop));
    });
  });
}

// =========================================================================
// BlockTree — full-height sidebar: palette + block list.
// Controlled selection: reads `selectedId`, reports changes via `onSelect`.
// =========================================================================

export default function BlockTree({ dsl, onDSLChange, selectedId, onSelect }) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteContainerRef = useRef(null);

  const onChangeRef = useRef(onDSLChange);
  onChangeRef.current = onDSLChange;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // ID-stable working copy. BlockTree owns propagation of minted IDs back to
  // the parent so the whole app (canvas overlay, ConfigPanel) sees stable IDs.
  const [workingDsl, needsIdPropagation] = useMemo(() => {
    const copy = JSON.parse(JSON.stringify(dsl));
    const changed = ensureIds(copy);
    return [copy, changed];
  }, [dsl]);

  useEffect(() => {
    if (needsIdPropagation) onChangeRef.current(workingDsl);
  }, [needsIdPropagation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expand ancestors of an externally-selected block so it's visible in the tree.
  useEffect(() => {
    if (selectedId != null) {
      const ancestors = findAncestors(workingDsl, selectedId) || [];
      setExpandedIds(prev => {
        const next = new Set(prev);
        for (const id of ancestors) next.add(id);
        return next;
      });
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand top-level containers when DSL changes.
  useEffect(() => {
    setExpandedIds(prev => {
      const ids = new Set(prev);
      if (workingDsl.bands) workingDsl.bands.forEach(b => ids.add(b.id || "band_0"));
      if (workingDsl.flow && Array.isArray(workingDsl.flow)) workingDsl.flow.forEach(f => { if (f.id) ids.add(f.id); });
      return ids;
    });
  }, [workingDsl]);

  // Close palette on outside click.
  useEffect(() => {
    if (!paletteOpen) return;
    const handler = (e) => {
      if (paletteContainerRef.current && !paletteContainerRef.current.contains(e.target)) {
        setPaletteOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [paletteOpen]);

  const palette = blockRegistry.getEditorPalette();
  const blocks = collectBlocks(workingDsl);
  const categories = (() => {
    const cats = {};
    for (const p of palette) {
      if (!cats[p.category]) cats[p.category] = [];
      cats[p.category].push(p);
    }
    return cats;
  })();

  // ── actions ──────────────────────────────────────────────────────────

  const addBlock = (blockType) => {
    const { dsl: newDSL, newId } = addBlockToDsl(dsl, blockType);
    onChangeRef.current(newDSL);
    onSelectRef.current?.(newId); // select the freshly added block (drives the panel)
    setPaletteOpen(false);
  };

  const removeBlock = (blockId) => {
    const next = removeBlockFromDsl(dsl, blockId);
    if (next) {
      onChangeRef.current(next);
      if (selectedId === blockId) onSelectRef.current?.(null); // dismiss panel + clear overlay
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── tree node ────────────────────────────────────────────────────────

  const renderTreeNode = (block, depth = 0) => {
    const blockId = block.id || "_noid";
    const def = blockRegistry.getIfRegistered(block.type);
    const label = def?.label || block.type || "block";
    const isContainer = block.type === "flow" || block.type === "band" || block.type === "conditional" || (!block.type && (block.columns || block.blocks));

    const childBlocks = [];
    if (block.children && Array.isArray(block.children)) childBlocks.push(...block.children);
    if (block.blocks && Array.isArray(block.blocks)) childBlocks.push(...block.blocks);
    if (block.columns && Array.isArray(block.columns)) {
      for (const col of block.columns) {
        if (col.blocks && Array.isArray(col.blocks)) childBlocks.push(...col.blocks);
        if (col.header) childBlocks.push({ type: "section_header", id: (col.id || "col") + "_header", text: col.header });
      }
    }
    if (block.branches && Array.isArray(block.branches)) {
      for (const br of block.branches) {
        if (br.then) childBlocks.push(br.then);
      }
    }

    const hasChildren = isContainer && childBlocks.length > 0;
    const isExpanded = expandedIds.has(blockId);
    const isSelected = selectedId === blockId;

    return (
      <div key={blockId} style={{ paddingLeft: depth * 16 }}>
        <div
          onClick={() => { onSelectRef.current?.(blockId); if (hasChildren) toggleExpand(blockId); }}
          className={cn(
            "group/node relative flex items-center gap-1.5 rounded-lg py-1.5 pr-1.5 pl-1.5 cursor-pointer transition-colors",
            isSelected
              ? "bg-ccgreen-50 text-ccgreen-900 ring-1 ring-inset ring-ccgreen-200"
              : "hover:bg-slate-100 text-slate-700"
          )}
        >
          {hasChildren ? (
            <Icon
              icon={isExpanded ? "lucide:chevron-down" : "lucide:chevron-right"}
              className={cn("h-3.5 w-3.5 shrink-0 transition-transform", isSelected ? "text-ccgreen-600" : "text-slate-400")}
            />
          ) : (
            <span className="w-3.5 shrink-0" />
          )}

          <span
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
              isContainer ? "text-slate-500 bg-slate-200/70" : categoryStyleFor(block.type)
            )}
          >
            <Icon icon={isContainer ? "lucide:box" : resolveBlockIcon(def?.editor?.icon)} className="h-3.5 w-3.5" />
          </span>

          <span className={cn("truncate text-[13px]", isSelected ? "font-semibold" : "font-medium")}>{label}</span>
          <span className={cn("truncate text-[11px]", isSelected ? "text-ccgreen-600/80" : "text-slate-400")}>#{blockId.slice(0, 14)}</span>

          {!isContainer && (
            <button
              onClick={(e) => { e.stopPropagation(); removeBlock(blockId); }}
              className={cn(
                "ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors",
                isSelected
                  ? "text-ccgreen-600 hover:bg-ccgreen-100"
                  : "text-slate-400 opacity-0 group-hover/node:opacity-100 hover:bg-rose-50 hover:text-rose-600"
              )}
              title="Remove block"
            >
              <Icon icon="lucide:trash-2" className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {hasChildren && isExpanded && childBlocks.map(child => renderTreeNode(child, depth + 1))}
      </div>
    );
  };

  // ── palette popup ────────────────────────────────────────────────────

  const renderPalette = () => {
    if (!paletteOpen) return null;
    return (
      <div className="absolute left-0 top-full z-[100] mt-1 max-h-[400px] min-w-[260px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg shadow-slate-900/10">
        {Object.entries(categories).map(([cat, items]) => (
          <div key={cat} className="mb-1 last:mb-0">
            <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {cat.replace(/-/g, " ")}
            </div>
            {items.map(p => (
              <button
                key={p.type}
                onClick={() => { addBlock(p.type); setPaletteOpen(false); }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-ccgreen-50"
              >
                <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-md", CATEGORY_STYLES[p.category] || CATEGORY_STYLES.layout)}>
                  <Icon icon={resolveBlockIcon(p.icon)} className="h-4 w-4" />
                </span>
                <span className="flex-1 text-[13px] font-medium text-slate-700">{p.label}</span>
                <code className="text-[10px] text-slate-400">{p.type}</code>
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // ── layout ───────────────────────────────────────────────────────────

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-slate-50 text-[13px]">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
        <span className="flex items-center gap-1.5">
          <Icon icon="lucide:layers" className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Blocks</span>
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">{blocks.length}</span>
        </span>
        <div ref={paletteContainerRef} className="relative ml-auto">
          <button
            onClick={() => setPaletteOpen(!paletteOpen)}
            className={cn(
              "flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold transition-colors",
              paletteOpen ? "bg-ccgreen-700 text-white" : "bg-ccgreen-600 text-white hover:bg-ccgreen-700"
            )}
          >
            <Icon icon="lucide:plus" className="h-3.5 w-3.5" />
            Add Block
          </button>
          {renderPalette()}
        </div>
      </div>

      {/* Tree — now full height; the config form lives in the floating panel. */}
      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
        {workingDsl.bands && workingDsl.bands.map((band, i) => (
          renderTreeNode({ ...band, type: band.type || "band", id: band.id || `band_${i}` }, 0)
        ))}
        {workingDsl.flow && Array.isArray(workingDsl.flow) && workingDsl.flow.map(child => renderTreeNode(child, 0))}
        {!workingDsl.flow && !workingDsl.bands && (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <Icon icon="lucide:blocks" className="h-6 w-6" />
            </span>
            <p className="text-sm font-medium text-slate-500">No blocks yet</p>
            <p className="text-xs text-slate-400">Click <span className="font-semibold text-ccgreen-600">Add Block</span> to start building.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// ConfigPanel — floating, contextual config form rendered over the canvas.
// Appears when a block is selected; ✕ (or deselecting) dismisses it. Anchors
// to the top-right of its positioned parent (the canvas area). The panel is
// keyed by selectedId in the parent, so switching blocks remounts it and
// resets the collapsible-field state to collapsed.
// =========================================================================

export function ConfigPanel({ dsl, onDSLChange, selectedId, onClose, side = "right" }) {
  const [shown, setShown] = useState(false);
  useEffect(() => { setShown(true); }, []); // triggers the slide/fade-in once

  const onChangeRef = useRef(onDSLChange);
  onChangeRef.current = onDSLChange;

  // ID-stable view for resolving the selected block. Doesn't propagate —
  // BlockTree owns ID minting; ensureIds here is an idempotent safety net.
  const workingDsl = useMemo(() => withEnsuredIds(dsl), [dsl]);
  const selectedBlock = collectBlocks(workingDsl).find(b => b.id === selectedId);

  // Guard: block vanished (e.g. removed via the tree while open). Render
  // nothing; the parent dismisses us via the remove path's onClose.
  if (!selectedBlock) return null;

  const def = blockRegistry.getIfRegistered(selectedBlock.type);
  const fields = visibleFieldsFor(selectedBlock);

  const setNestedValue = (path, value) => {
    onChangeRef.current(setNestedValueInDsl(dsl, selectedId, path, value));
  };
  const removeBlock = () => {
    const next = removeBlockFromDsl(dsl, selectedId);
    if (next) { onChangeRef.current(next); onClose(); }
  };

  return (
    <div
      className={cn(
        "absolute top-4 z-30 flex max-h-[calc(100%-2rem)] w-[320px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/15 transition-all duration-200 ease-out",
        // Position-aware: anchor on the opposite side from the selected block
        // so the panel never covers what the user clicked. Slide-in direction
        // matches the anchor (slides in from the anchored edge).
        side === "left" ? "left-4" : "right-4",
        shown ? "translate-x-0 opacity-100" : (side === "left" ? "-translate-x-2 opacity-0" : "translate-x-2 opacity-0")
      )}
    >
      {/* Header — sticky, never scrolls. Close (✕) is the primary dismiss;
          remove (trash) is a separate destructive action. */}
      <div className="flex items-center gap-2.5 border-b border-slate-200 bg-white/95 px-3 py-2.5 backdrop-blur">
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", categoryStyleFor(selectedBlock.type))}>
          <Icon icon={resolveBlockIcon(def?.editor?.icon)} className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-800">{def?.label || selectedBlock.type}</div>
          <code className="truncate text-[11px] text-slate-400">{selectedBlock.id}</code>
        </div>
        <button
          onClick={removeBlock}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
          title="Remove block"
        >
          <Icon icon="lucide:trash-2" className="h-4 w-4" />
        </button>
        <button
          onClick={onClose}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          title="Close"
        >
          <Icon icon="lucide:x" className="h-4 w-4" />
        </button>
      </div>

      {/* Body — fields scroll independently; header stays put. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {fields.length > 0 ? (
          <div className="space-y-2.5">
            {fields.map(field => (
              <FieldEditor
                key={field.name}
                field={field}
                value={selectedBlock[field.name]}
                onChange={(path, val) => setNestedValue(path, val)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-400">
            No configurable properties for this block.
          </div>
        )}
      </div>
    </div>
  );
}
